<template>
  <div class="survey-form-container">
    <!-- SUCCESS SCREEN -->
    <el-card v-if="isCompletedSuccess" class="success-card" shadow="always">
      <div class="success-content">
        <div class="success-icon-wrap">
          <i class="el-icon-circle-check success-icon"></i>
        </div>
        <h2 class="success-title">Дякуємо за участь в опитуванні!</h2>
        <p class="success-text">
          Ваші відповіді успішно зафіксовано в системі. Отримана інформація допоможе покращити якість послуг з розподілу природного газу відповідно до стандартів НКРЕКП.
        </p>
        <div class="success-meta">
          <p><strong>EIC-код:</strong> {{ consumer.eic }}</p>
          <p><strong>Дата завершення:</strong> {{ formatDateTime(completionTime) }}</p>
        </div>
        <div class="success-actions">
          <el-button type="primary" size="medium" @click="goToHome">
            <i class="el-icon-s-home"></i> {{ isUser ? 'В особистий кабінет' : 'На головну сторінку' }}
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- ALREADY VOTED SCREEN -->
    <el-card v-else-if="isAlreadyVoted" class="already-voted-card" shadow="always">
      <div class="already-voted-content">
        <div class="warning-icon-wrap">
          <i class="el-icon-warning warning-icon"></i>
        </div>
        <h2 class="warning-title">Повторне голосування заборонено</h2>
        <p class="warning-text">
          {{ alreadyVotedMessage || 'За цим EIC-кодом уже було успішно надіслано відповіді на опитування. Відповідно до вимог НКРЕКП, кожен споживач може пройти опитування лише один раз.' }}
        </p>
        <div class="consumer-meta" v-if="consumer.eic">
          <p><strong>EIC-код:</strong> {{ consumer.eic }}</p>
          <p v-if="consumer.contractNumber"><strong>{{ consumer.consumerType === 'household' ? 'Особовий рахунок' : '№ Договору' }}:</strong> {{ consumer.contractNumber }}</p>
          <p v-if="consumer.name"><strong>Споживач / Контрагент:</strong> {{ consumer.name }}</p>
        </div>
        <div class="action-buttons">
          <el-button type="primary" size="medium" @click="goToHome">
            <i class="el-icon-s-home"></i> {{ isUser ? 'В особистий кабінет' : 'На головну сторінку' }}
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- SURVEY EXPIRED OR INACTIVE SCREEN -->
    <el-card v-else-if="isSurveyExpiredOrInactive" class="expired-survey-card" shadow="always">
      <div class="expired-content">
        <div class="expired-icon-wrap">
          <i class="el-icon-time expired-icon"></i>
        </div>
        <h2 class="expired-title">{{ expiredTitle }}</h2>
        <p class="expired-text">
          {{ expiredMessage }}
        </p>
        <div v-if="survey && survey.endDate" class="period-meta">
          <p><strong>Встановлений період проведення:</strong> {{ formatDate(survey.startDate) }} – {{ formatDate(survey.endDate) }}</p>
        </div>
        <div class="action-buttons">
          <el-button type="primary" size="medium" @click="goToHome">
            <i class="el-icon-s-home"></i> {{ isUser ? 'В особистий кабінет' : 'На головну сторінку' }}
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- MAIN SURVEY FORM -->
    <div v-else-if="survey" class="survey-main-wrap">
      <el-card class="survey-header-card" shadow="never">
        <div class="survey-title-block">
          <div class="badge-row">
            <el-tag :type="survey.consumerType === 'household' ? 'success' : 'warning'" effect="dark" size="small">
              {{ survey.consumerType === 'household' ? 'Побутовий споживач' : 'Непобутовий споживач' }}
            </el-tag>
            <span class="period-badge">
              <i class="el-icon-date"></i> Період: {{ formatDate(survey.startDate) }} – {{ formatDate(survey.endDate) }}
            </span>
          </div>
          <h1 class="main-title">{{ survey.title }}</h1>
        </div>

        <el-alert
          :title="survey.description || 'Опитування проводиться у зв\'язку із запитом НКРЕКП. Усі надані відповіді є конфіденційними.'"
          type="info"
          show-icon
          :closable="false"
          class="description-alert"
        />

        <!-- Pre-filled Consumer Information -->
        <div v-if="consumer.eic" class="consumer-info-banner">
          <div class="banner-title"><i class="el-icon-info"></i> Дані споживача (заповнено автоматично за EIC-кодом)</div>
          <div class="banner-grid">
            <div class="info-item">
              <span class="info-label"><i class="el-icon-key"></i> EIC-код:</span>
              <span class="info-value font-mono">{{ consumer.eic }}</span>
            </div>
            <div v-if="consumer.contractNumber" class="info-item">
              <span class="info-label"><i class="el-icon-document"></i> {{ consumer.consumerType === 'household' ? 'Особовий рахунок' : '№ Договору' }}:</span>
              <span class="info-value font-bold">{{ consumer.contractNumber }}</span>
            </div>
            <div v-if="consumer.name" class="info-item full-width">
              <span class="info-label"><i class="el-icon-office-building"></i> Споживач / Контрагент:</span>
              <span class="info-value">{{ consumer.name }}</span>
            </div>
            <div class="info-item">
              <span class="info-label"><i class="el-icon-user"></i> Категорія:</span>
              <span class="info-value">{{ consumer.consumerType === 'household' ? 'Побутовий споживач' : 'Непобутовий споживач' }}</span>
            </div>
            <div v-if="consumer.consumerGroup" class="info-item">
              <span class="info-label"><i class="el-icon-collection-tag"></i> Група:</span>
              <span class="info-value">{{ consumer.consumerGroup }}</span>
            </div>
            <div class="info-item">
              <span class="info-label"><i class="el-icon-location-outline"></i> Населений пункт:</span>
              <span class="info-value">{{ consumer.settlementType || 'місто' }} {{ consumer.settlementName || 'Тернопіль' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label"><i class="el-icon-odometer"></i> Фактичний річний об'єм:</span>
              <span class="info-value">{{ (consumer.annualConsumption || 0).toLocaleString() }} м³</span>
            </div>
            <div class="info-item">
              <span class="info-label"><i class="el-icon-c-scale-to-original"></i> Лічильник:</span>
              <span class="info-value">{{ consumer.hasMeter !== false ? ('Наявний (' + (consumer.meterType || 'G-4') + ')') : 'Без лічильника' }}</span>
            </div>
          </div>
        </div>
      </el-card>

      <!-- SURVEY BLOCKS -->
      <el-form ref="surveyForm" label-position="top" class="survey-questions-form">
        <div
          v-for="(block, bIndex) in survey.blocks"
          :key="block.code || bIndex"
          class="survey-block-section"
        >
          <div class="block-header">
            <span class="block-number">{{ bIndex + 1 }}</span>
            <h3 class="block-title">{{ block.title }}</h3>
          </div>

          <div class="questions-list">
            <div
              v-for="question in block.questions"
              :key="question.code"
              v-show="isQuestionVisible(question)"
              class="question-card"
              :class="{ 'has-error': isQuestionError(question.code), 'is-dependent': !!question.parentQuestionCode }"
            >
              <div class="question-header">
                <span class="question-code-badge">{{ question.code }}</span>
                <span class="question-text">
                  {{ question.text }}
                  <span v-if="question.required" class="required-star">*</span>
                </span>
              </div>

              <div class="question-body">
                <!-- 1. YES / NO TYPE -->
                <div v-if="question.type === 'yes_no'" class="control-wrap">
                  <el-radio-group
                    :value="getAnswerValue(question.code)"
                    @input="(val) => setAnswerValue(question, val)"
                    size="medium"
                  >
                    <el-radio-button label="Так">
                      <i class="el-icon-check"></i> Так
                    </el-radio-button>
                    <el-radio-button label="Ні">
                      <i class="el-icon-close"></i> Ні
                    </el-radio-button>
                  </el-radio-group>
                </div>

                <!-- 2. SINGLE CHOICE TYPE -->
                <div v-else-if="question.type === 'single_choice'" class="control-wrap vertical-options">
                  <el-radio-group
                    :value="getAnswerValue(question.code)"
                    @input="(val) => setAnswerValue(question, val)"
                  >
                    <div v-for="(opt, oIndex) in question.options" :key="oIndex" class="option-row">
                      <el-radio :label="opt">{{ opt }}</el-radio>
                    </div>
                    <div v-if="question.specialOption" class="option-row special-option">
                      <el-radio :label="question.specialOption">
                        <em>{{ question.specialOption }}</em>
                      </el-radio>
                    </div>
                    <div v-if="question.hasOtherOption" class="option-row">
                      <el-radio label="Інше">Інше (уточніть):</el-radio>
                      <el-input
                        v-if="getAnswerValue(question.code) === 'Інше'"
                        :value="getAnswerOther(question.code)"
                        @input="(val) => setAnswerOther(question.code, val)"
                        placeholder="Введіть ваш варіант"
                        size="small"
                        class="other-input"
                      />
                    </div>
                  </el-radio-group>
                </div>

                <!-- 3. MULTIPLE CHOICE TYPE -->
                <div v-else-if="question.type === 'multiple_choice'" class="control-wrap vertical-options">
                  <el-checkbox-group
                    :value="getAnswerArrayValue(question.code)"
                    @input="(val) => setAnswerArrayValue(question, val)"
                  >
                    <div v-for="(opt, oIndex) in question.options" :key="oIndex" class="option-row">
                      <el-checkbox :label="opt">{{ opt }}</el-checkbox>
                    </div>
                    <div v-if="question.hasOtherOption" class="option-row">
                      <el-checkbox label="Інше">Інше (уточніть):</el-checkbox>
                      <el-input
                        v-if="isOtherSelected(question.code)"
                        :value="getAnswerOther(question.code)"
                        @input="(val) => setAnswerOther(question.code, val)"
                        placeholder="Введіть ваш варіант"
                        size="small"
                        class="other-input"
                      />
                    </div>
                  </el-checkbox-group>
                </div>

                <!-- 4. SCALE 1 TO 10 TYPE -->
                <div v-else-if="question.type === 'scale_1_10'" class="control-wrap scale-wrap">
                  <div class="scale-buttons">
                    <button
                      v-for="num in 10"
                      :key="num"
                      type="button"
                      class="scale-btn"
                      :class="{ active: getAnswerValue(question.code) === num }"
                      @click="setAnswerValue(question, num)"
                    >
                      {{ num }}
                    </button>
                  </div>
                  <div class="scale-labels">
                    <span>1 — Дуже низько</span>
                    <span>10 — Відмінно</span>
                  </div>
                  <div v-if="question.specialOption" class="scale-special-btn-wrap">
                    <el-button
                      size="small"
                      :type="getAnswerValue(question.code) === question.specialOption ? 'primary' : 'default'"
                      plain
                      @click="setAnswerValue(question, question.specialOption)"
                    >
                      <i class="el-icon-question"></i> {{ question.specialOption }}
                    </el-button>
                  </div>
                </div>

                <!-- 5. SCALE SPECIAL TYPE -->
                <div v-else-if="question.type === 'scale_special'" class="control-wrap scale-wrap">
                  <div class="scale-buttons">
                    <button
                      v-for="num in 10"
                      :key="num"
                      type="button"
                      class="scale-btn"
                      :class="{ active: getAnswerValue(question.code) === num }"
                      @click="setAnswerValue(question, num)"
                    >
                      {{ num }}
                    </button>
                  </div>
                  <div class="scale-labels">
                    <span>1 — Незадовільно</span>
                    <span>10 — Відмінно</span>
                  </div>
                  <div class="scale-special-btn-wrap">
                    <el-button
                      size="small"
                      :type="getAnswerValue(question.code) === (question.specialOption || 'Не було технічного обслуговування') ? 'primary' : 'default'"
                      plain
                      @click="setAnswerValue(question, question.specialOption || 'Не було технічного обслуговування')"
                    >
                      <i class="el-icon-circle-close"></i> {{ question.specialOption || 'Не було технічного обслуговування' }}
                    </el-button>
                  </div>
                </div>

                <!-- 6. TEXT TYPE -->
                <div v-else-if="question.type === 'text'" class="control-wrap">
                  <el-input
                    type="textarea"
                    :rows="3"
                    :value="getAnswerValue(question.code)"
                    @input="(val) => setAnswerValue(question, val)"
                    placeholder="Введіть вашу відповідь..."
                    maxlength="1000"
                    show-word-limit
                  />
                </div>

                <!-- 7. NUMBER TYPE -->
                <div v-else-if="question.type === 'number'" class="control-wrap">
                  <el-input-number
                    :value="getAnswerValue(question.code)"
                    @input="(val) => setAnswerValue(question, val)"
                    :min="0"
                    :max="1000000"
                    size="medium"
                  />
                </div>
              </div>

              <div v-if="isQuestionError(question.code)" class="field-error-msg">
                <i class="el-icon-warning"></i> Будь ласка, дайте відповідь на це обов'язкове запитання.
              </div>
            </div>
          </div>
        </div>

        <!-- SUBMIT SECTION -->
        <div class="submit-card-section">
          <div v-if="formErrorMessage" class="form-error-alert">
            <el-alert
              :title="formErrorMessage"
              type="error"
              show-icon
              :closable="false"
            />
          </div>

          <el-button
            type="primary"
            size="large"
            class="final-submit-btn"
            :loading="isLoading"
            @click="handleSubmit"
          >
            <i class="el-icon-check"></i> Завершити та надіслати опитування
          </el-button>
        </div>
      </el-form>
    </div>

    <!-- LOADING STATE -->
    <div v-else class="loading-wrap">
      <i class="el-icon-loading"></i> Завантаження опитування...
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';

export default {
  name: 'SurveyForm',
  data() {
    return {
      answersMap: {}, // key: questionCode -> { value, otherText, questionCode, questionText, blockCode, blockTitle, questionType }
      validationErrors: new Set(),
      formErrorMessage: '',
      isCompletedSuccess: false,
      isAlreadyVoted: false,
      alreadyVotedMessage: '',
      completionTime: null,
      consumer: {
        eic: '',
        contractNumber: '',
        name: '',
        consumerType: 'household',
        consumerGroup: '',
        region: 'Тернопільська',
        settlementType: 'місто',
        settlementName: 'Тернопіль',
        annualConsumption: 0,
        hasMeter: true,
        meterType: 'G-4',
        hasRemoteDataTransfer: false,
      },
    };
  },
  computed: {
    ...mapGetters(['isUser']),
    ...mapGetters('Survey', ['currentSurvey', 'consumerData', 'isLoading']),
    survey() {
      return this.currentSurvey;
    },
    isSurveyExpiredOrInactive() {
      if (!this.survey) return false;
      if (this.survey.status !== 'ACTIVE') return true;
      if (this.survey.isPeriodExpired) return true;
      if (this.survey.endDate && new Date(this.survey.endDate) < new Date()) return true;
      return false;
    },
    expiredTitle() {
      if (this.survey && this.survey.status !== 'ACTIVE') {
        return 'Опитування наразі неактивне';
      }
      return 'Період проведення опитування завершено';
    },
    expiredMessage() {
      if (this.survey && this.survey.status !== 'ACTIVE') {
        return 'Це опитування наразі деактивовано або завершено адміністратором. Прийом відповідей зупинено.';
      }
      return 'Термін проведення цього опитування вже завершився відповідно до графіка НКРЕКП. Прийом відповідей припинено. Дякуємо за вашу увагу до роботи Оператора ГРМ!';
    },
  },
  async created() {
    // 1. If consumer data already exists in store, use it
    if (this.consumerData && this.consumerData.eic) {
      this.consumer = { ...this.consumerData };
    }

    // Check for query parameters: account number and/or eic
    const queryAccount = this.$route.query.accountNumber ||
                         this.$route.query.account ||
                         this.$route.query.contractNumber ||
                         this.$route.query.acc ||
                         this.$route.query.rahunok ||
                         this.$route.query.ls;
    const queryEic = this.$route.query.eic ? String(this.$route.query.eic).toUpperCase() : '';

    if (queryAccount && !this.consumer.contractNumber) {
      this.consumer.contractNumber = String(queryAccount).trim();
    }

    if (!this.consumer.eic) {
      if (queryEic) {
        this.consumer.eic = queryEic;
        try {
          const verifyRes = await this.verifyEic({
            eic: this.consumer.eic,
            accountNumber: queryAccount,
            surveyType: 'auto',
          });
          if (verifyRes && verifyRes.consumer) {
            this.consumer = { ...verifyRes.consumer };
          }
        } catch (err) {
          if (err.response && err.response.data) {
            if (err.response.data.alreadyVoted) {
              this.isAlreadyVoted = true;
              this.alreadyVotedMessage = err.response.data.error;
              return;
            }
          }
        }
      } else if (queryAccount) {
        try {
          const verifyRes = await this.verifyEic({
            accountNumber: queryAccount,
            surveyType: 'household',
          });
          if (verifyRes && verifyRes.consumer) {
            this.consumer = { ...verifyRes.consumer };
          }
        } catch (err) {
          if (err.response && err.response.data) {
            if (err.response.data.alreadyVoted) {
              this.isAlreadyVoted = true;
              this.alreadyVotedMessage = err.response.data.error;
              return;
            }
          }
        }
      }
    }

    // 2. Fetch survey if not loaded or if logged in as user
    const surveyId = this.$route.params.id;
    if (!this.currentSurvey || this.currentSurvey._id !== surveyId) {
      if (surveyId) {
        try {
          await this.fetchSurveyById(surveyId);
        } catch (e) {
          this.$message.error('Не вдалося завантажити структуру опитування');
        }
      } else {
        // Fetch active survey
        try {
          const reqType = this.isUser ? 'non_household' : (this.consumer.consumerType || 'household');
          const activeRes = await this.fetchActiveSurvey(reqType);
          if (activeRes) {
            if (activeRes.isCompleted) {
              this.isAlreadyVoted = true;
              this.alreadyVotedMessage = 'Ви вже успішно взяли участь у цьому опитуванні. Відповідно до вимог НКРЕКП, повторне голосування не допускається.';
            }
            if (activeRes.consumer) {
              this.consumer = { ...activeRes.consumer };
            }
          }
        } catch (e) {
          this.$message.error('Активне опитування не знайдено');
        }
      }
    } else if (this.isUser && !this.consumer.eic) {
      // Ensure consumer details are loaded for the current authorized company
      try {
        const activeRes = await this.fetchActiveSurvey('non_household');
        if (activeRes && activeRes.consumer) {
          this.consumer = { ...activeRes.consumer };
        }
        if (activeRes && activeRes.isCompleted) {
          this.isAlreadyVoted = true;
        }
      } catch (e) {
        console.warn('Could not refresh consumer details:', e.message);
      }
    }

    // 3. Auto-fill questions from known consumer data
    this.$nextTick(() => {
      this.autoFillFromConsumer();
    });
  },
  methods: {
    ...mapActions('Survey', ['fetchSurveyById', 'fetchActiveSurvey', 'submitSurvey', 'verifyEic']),

    autoFillFromConsumer() {
      if (!this.survey || !this.survey.blocks || !this.consumer) return;

      this.survey.blocks.forEach((block) => {
        if (!block.questions) return;
        block.questions.forEach((q) => {
          // If already answered, don't overwrite
          if (this.answersMap[q.code] && this.answersMap[q.code].value !== null) return;

          // Remote data transfer question
          if (q.code === 'NH_Q1') {
            if (this.consumer.hasRemoteDataTransfer) {
              this.setAnswerValue(q, 'Так, встановлено коректор/модем із дистанційною передачею');
            } else {
              this.setAnswerValue(q, 'Ні, передача показників здійснюється вручну');
            }
          }

          // Meter presence
          const qText = (q.text || '').toLowerCase();
          if (qText.includes('наявність лічильника') || (qText.includes('лічильник') && q.type === 'yes_no')) {
            if (q.type === 'yes_no') {
              this.setAnswerValue(q, this.consumer.hasMeter !== false ? 'Так' : 'Ні');
            }
          }
        });
      });
    },

    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('DD.MM.YYYY');
    },

    formatDateTime(date) {
      if (!date) return '';
      return dayjs(date).format('DD.MM.YYYY HH:mm');
    },

    getAnswerValue(code) {
      return this.answersMap[code] ? this.answersMap[code].value : null;
    },

    getAnswerArrayValue(code) {
      if (this.answersMap[code] && Array.isArray(this.answersMap[code].value)) {
        return this.answersMap[code].value;
      }
      return [];
    },

    getAnswerOther(code) {
      return this.answersMap[code] ? this.answersMap[code].otherText : '';
    },

    isOtherSelected(code) {
      const arr = this.getAnswerArrayValue(code);
      return arr.includes('Інше');
    },

    setAnswerValue(question, val) {
      this.$set(this.answersMap, question.code, {
        questionCode: question.code,
        questionText: question.text,
        blockCode: question.blockCode,
        blockTitle: question.blockTitle,
        questionType: question.type,
        value: val,
        otherText: (this.answersMap[question.code] && this.answersMap[question.code].otherText) || '',
      });
      this.validationErrors.delete(question.code);
      this.formErrorMessage = '';
    },

    setAnswerArrayValue(question, arr) {
      this.$set(this.answersMap, question.code, {
        questionCode: question.code,
        questionText: question.text,
        blockCode: question.blockCode,
        blockTitle: question.blockTitle,
        questionType: question.type,
        value: arr,
        otherText: (this.answersMap[question.code] && this.answersMap[question.code].otherText) || '',
      });
      this.validationErrors.delete(question.code);
      this.formErrorMessage = '';
    },

    setAnswerOther(code, text) {
      if (this.answersMap[code]) {
        this.answersMap[code].otherText = text;
      }
    },

    isQuestionVisible(q) {
      if (!q.parentQuestionCode) return true;

      const parentAns = this.answersMap[q.parentQuestionCode];
      if (!parentAns || parentAns.value === undefined || parentAns.value === null) {
        return false;
      }

      if (q.parentAnswerTrigger === null || q.parentAnswerTrigger === undefined) {
        return true;
      }

      const expectedTriggers = Array.isArray(q.parentAnswerTrigger)
        ? q.parentAnswerTrigger
        : [q.parentAnswerTrigger];

      if (Array.isArray(parentAns.value)) {
        return parentAns.value.some((val) =>
          expectedTriggers.some((t) => String(t).trim().toLowerCase() === String(val).trim().toLowerCase())
        );
      }

      return expectedTriggers.some(
        (t) => String(t).trim().toLowerCase() === String(parentAns.value).trim().toLowerCase()
      );
    },

    isQuestionError(code) {
      return this.validationErrors.has(code);
    },

    validateAnswers() {
      this.validationErrors.clear();
      this.formErrorMessage = '';

      if (!this.survey || !this.survey.blocks) return true;

      for (const block of this.survey.blocks) {
        if (block.questions) {
          for (const q of block.questions) {
            const visible = this.isQuestionVisible(q);
            if (visible && q.required) {
              const ans = this.answersMap[q.code];
              if (
                !ans ||
                ans.value === undefined ||
                ans.value === null ||
                ans.value === '' ||
                (Array.isArray(ans.value) && ans.value.length === 0)
              ) {
                this.validationErrors.add(q.code);
              }
            }
          }
        }
      }

      if (this.validationErrors.size > 0) {
        this.formErrorMessage = `Будь ласка, заповніть усі обов'язкові поля (залишилось незаповнених: ${this.validationErrors.size}).`;
        this.$nextTick(() => {
          const firstErrElem = document.querySelector('.question-card.has-error');
          if (firstErrElem) {
            firstErrElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return false;
      }

      return true;
    },

    async handleSubmit() {
      if (!this.validateAnswers()) return;

      const answersPayload = [];
      if (this.survey && this.survey.blocks) {
        this.survey.blocks.forEach((b) => {
          if (b.questions) {
            b.questions.forEach((q) => {
              if (this.isQuestionVisible(q) && this.answersMap[q.code]) {
                answersPayload.push(this.answersMap[q.code]);
              }
            });
          }
        });
      }

      try {
        const payload = {
          surveyId: this.survey._id,
          eic: this.consumer.eic || (this.$route.query && this.$route.query.eic),
          entrySource: this.$route.query.entry === 'web_link' ? 'web_link' : 'portal',
          consumerData: this.consumer,
          answers: answersPayload,
        };

        const result = await this.submitSurvey(payload);
        this.isCompletedSuccess = true;
        this.completionTime = result.completedAt || new Date();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        if (err.response && err.response.data) {
          this.formErrorMessage = err.response.data.error || 'Помилка при відправці відповідей.';
          if (err.response.data.alreadyVoted) {
            this.isAlreadyVoted = true;
            this.alreadyVotedMessage = err.response.data.error;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          this.formErrorMessage = 'Помилка при відправці відповідей. Спробуйте ще раз.';
        }
      }
    },

    goToHome() {
      if (this.isUser) {
        this.$router.push('/');
      } else {
        this.$router.push('/survey/entry');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.survey-form-container {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 16px 60px 16px;
}

.already-voted-card {
  border-radius: 16px;
  text-align: center;
  padding: 40px 20px;
  background: #ffffff;
  border-top: 6px solid #e6a23c;

  .warning-icon-wrap {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    background-color: #fdf6ec;
    border-radius: 50%;
    margin-bottom: 20px;

    .warning-icon {
      font-size: 48px;
      color: #e6a23c;
    }
  }

  .warning-title {
    font-size: 24px;
    color: #303133;
    margin-bottom: 12px;
    font-weight: 700;
  }

  .warning-text {
    font-size: 15px;
    color: #606266;
    max-width: 600px;
    margin: 0 auto 24px auto;
    line-height: 1.6;
  }

  .consumer-meta {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 14px;
    max-width: 500px;
    margin: 0 auto 24px auto;
    text-align: left;
    p {
      margin: 4px 0;
      font-size: 13px;
      color: #475569;
    }
  }
}

.expired-survey-card {
  border-radius: 16px;
  text-align: center;
  padding: 40px 20px;
  background: #ffffff;
  border-top: 6px solid #f56c6c;

  .expired-icon-wrap {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    background-color: #fef0f0;
    border-radius: 50%;
    margin-bottom: 20px;

    .expired-icon {
      font-size: 44px;
      color: #f56c6c;
    }
  }

  .expired-title {
    font-size: 24px;
    color: #1e293b;
    margin-bottom: 12px;
    font-weight: 700;
  }

  .expired-text {
    font-size: 15px;
    color: #64748b;
    max-width: 580px;
    margin: 0 auto 20px auto;
    line-height: 1.6;
  }

  .period-meta {
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    padding: 12px 20px;
    display: inline-block;
    margin-bottom: 24px;

    p {
      margin: 0;
      font-size: 13px;
      color: #334155;
    }
  }
}

.loading-wrap {
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: #6b7280;
}

.survey-header-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
  padding: 10px;

  .badge-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;

    .period-badge {
      font-size: 13px;
      color: #64748b;
    }
  }

  .main-title {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.35;
    margin: 8px 0 16px 0;
  }

  .description-alert {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .consumer-info-banner {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #10b981;
    border-radius: 8px;
    padding: 14px 18px;

    .banner-title {
      font-size: 13px;
      font-weight: 700;
      color: #047857;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .banner-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 10px 16px;

      .info-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;

        &.full-width {
          grid-column: 1 / -1;
        }

        .info-label {
          color: #64748b;
          font-weight: 500;
          white-space: nowrap;
        }

        .info-value {
          color: #1e293b;
          font-weight: 600;

          &.font-mono {
            font-family: monospace;
            background: #e2e8f0;
            padding: 2px 6px;
            border-radius: 4px;
          }

          &.font-bold {
            font-weight: 700;
            color: #0f172a;
          }
        }
      }
    }
  }
}

.survey-block-section {
  margin-bottom: 32px;

  .block-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #3b82f6;

    .block-number {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background-color: #3b82f6;
      color: #fff;
      font-weight: 700;
      border-radius: 50%;
      font-size: 14px;
    }

    .block-title {
      font-size: 18px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }
  }
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.question-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &.is-dependent {
    margin-left: 20px;
    background: #f8fafc;
    border-left: 4px solid #3b82f6;
  }

  &.has-error {
    border-color: #ef4444 !important;
    background: #fef2f2;
  }

  .question-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 14px;

    .question-code-badge {
      font-size: 12px;
      font-weight: 700;
      color: #475569;
      background: #e2e8f0;
      padding: 2px 6px;
      border-radius: 4px;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .question-text {
      font-size: 16px;
      font-weight: 600;
      color: #1e293b;
      line-height: 1.4;

      .required-star {
        color: #ef4444;
        font-weight: 700;
        margin-left: 4px;
      }
    }
  }

  .question-body {
    .control-wrap {
      margin-top: 8px;

      &.vertical-options {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .option-row {
          padding: 4px 0;

          &.special-option {
            border-top: 1px dashed #e2e8f0;
            padding-top: 10px;
            margin-top: 6px;
          }

          .other-input {
            margin-top: 8px;
            max-width: 400px;
          }
        }
      }

      &.scale-wrap {
        .scale-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;

          .scale-btn {
            width: 44px;
            height: 44px;
            border: 1px solid #cbd5e1;
            background: #fff;
            color: #1e293b;
            font-size: 16px;
            font-weight: 600;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
              border-color: #3b82f6;
              color: #3b82f6;
              background: #eff6ff;
            }

            &.active {
              background: #3b82f6;
              color: #ffffff;
              border-color: #3b82f6;
              box-shadow: 0 2px 6px rgba(59, 130, 246, 0.35);
            }
          }
        }

        .scale-labels {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #64748b;
          margin-top: 8px;
          max-width: 494px;
        }

        .scale-special-btn-wrap {
          margin-top: 12px;
        }
      }
    }
  }

  .field-error-msg {
    margin-top: 10px;
    font-size: 13px;
    color: #ef4444;
    font-weight: 500;
  }
}

.submit-card-section {
  text-align: center;
  padding: 24px 0;

  .form-error-alert {
    margin-bottom: 20px;
    text-align: left;
  }

  .final-submit-btn {
    min-width: 320px;
    height: 52px;
    font-size: 17px;
    font-weight: 700;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  }
}

.success-card {
  max-width: 600px;
  margin: 40px auto;
  border-radius: 12px;
  text-align: center;
  padding: 20px;

  .success-content {
    .success-icon-wrap {
      margin-bottom: 16px;
      .success-icon {
        font-size: 64px;
        color: #10b981;
      }
    }

    .success-title {
      font-size: 24px;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 12px 0;
    }

    .success-text {
      font-size: 15px;
      color: #475569;
      line-height: 1.5;
      margin-bottom: 24px;
    }

    .success-meta {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 16px;
      display: inline-block;
      text-align: left;
      margin-bottom: 24px;

      p {
        margin: 4px 0;
        font-size: 14px;
        color: #334155;
      }
    }

    .success-actions {
      margin-top: 10px;
    }
  }
}
</style>
